import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  LineChart,
  Bell,
  User,
  Menu,
  X,
  Rss,
  PlusSquare,
  Users,
  UserCheck,
  Eye,
  Briefcase,
  Crown,
  Settings,
  Shield,
  Newspaper,
} from "lucide-react";

// Every destination that only lives inside the "More" sheet -- used to
// decide whether the More tab itself should show as active, since none
// of the other 4 fixed icons light up for e.g. /watchlist or /portfolio.
const MORE_ROUTES = [
  "/stockers",
  "/post-create",
  "/groupies",
  "/groups",
  "/connections",
  "/watchlist",
  "/portfolio",
  "/leaderboard",
  "/news",
  "/settings",
  "/admin",
];

const MobileBottomBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useSelector((state) => state.featureFlags.flags);
  const { user } = useSelector((state) => state.profile);
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path) => location.pathname.startsWith(path);
  const isMoreActive = MORE_ROUTES.some((path) => isActive(path));

  // Escape closes, matching MobileSearchOverlay's convention
  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [moreOpen]);

  const itemClass = (active) =>
    `flex flex-col items-center justify-center gap-1 text-xs transition-colors ${
      active ? "text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-slate-400"
    }`;

  const go = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  /* ============================
     DEFAULT NAV BAR
     ============================ */
  const DefaultBar = () => (
    <>
      <button
        onClick={() => navigate("/dashboard")}
        className={itemClass(isActive("/dashboard"))}
      >
        <Home size={18} />
        {t('nav.home')}
      </button>

      <button
        onClick={() => navigate("/stocks-overview")}
        className={itemClass(isActive("/stocks-overview"))}
      >
        <LineChart size={18} />
        {t('sidebar.stocks')}
      </button>

      <button
        onClick={() => setMoreOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        className={itemClass(isMoreActive)}
      >
        <Menu size={18} />
        {t('nav.more')}
      </button>

      <button
        onClick={() => navigate("/notifications")}
        className={itemClass(isActive("/notifications"))}
      >
        <Bell size={18} />
        {t('nav.alerts')}
      </button>

      <button
        onClick={() => navigate("/profile")}
        className={itemClass(isActive("/profile"))}
      >
        <User size={18} />
        {t('sidebar.profile')}
      </button>
    </>
  );

  /* ============================
     MORE SHEET
     Everything the desktop sidebar has that doesn't fit in the 5-icon
     default bar -- Watchlist/Portfolio/Leaderboard never had a mobile
     entry point at all before this. Icons deliberately match the
     desktop sidebar's own icons 1:1 (leftSideBar.jsx / its account
     menu) so the same destination looks the same at any breakpoint.
     ============================ */
  const moreItems = [
    featureFlags.posts && { icon: Rss, label: t('nav.feed'), path: "/stockers" },
    featureFlags.posts && { icon: PlusSquare, label: t('sidebar.post'), path: "/post-create" },
    { icon: Users, label: t('sidebar.groups'), path: "/groupies" },
    { icon: UserCheck, label: t('nav.connects'), path: "/connections/me" },
    { icon: Eye, label: t('sidebar.watchlist'), path: "/watchlist" },
    { icon: Briefcase, label: t('sidebar.portfolio'), path: "/portfolio" },
    { icon: Crown, label: t('sidebar.leaderboard'), path: "/leaderboard" },
    { icon: Newspaper, label: t('sidebar.news'), path: "/news" },
    { icon: Settings, label: t('nav.settings'), path: "/settings" },
    user?.role === "admin" && { icon: Shield, label: t('nav.admin'), path: "/admin" },
  ].filter(Boolean);

  const MoreSheet = () => (
    <div
      className="fixed inset-0 bg-black/50 z-[10000] md:hidden"
      onClick={(e) => {
        if (e.target === e.currentTarget) setMoreOpen(false);
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.moreNavigation')}
    >
      <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-2xl border-t border-gray-200 dark:border-slate-800 pb-[max(1rem,env(safe-area-inset-bottom))] max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('nav.more')}</h2>
          <button
            onClick={() => setMoreOpen(false)}
            aria-label={t('common.close')}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 px-4 pb-4">
          {moreItems.map(({ icon: Icon, label, path }) => {
            const active = isActive(path) || (path === "/groupies" && isActive("/groups"));
            return (
              <button
                key={path}
                onClick={() => go(path)}
                className={`flex flex-col items-center justify-center gap-1.5 py-4 rounded-xl text-xs font-medium transition-colors ${
                  active
                    ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                }`}
              >
                <Icon size={20} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <nav aria-label="Mobile navigation" className="h-16 bg-white border-t border-gray-200 dark:bg-gray-900 dark:border-slate-800">
      <div className="grid h-full grid-cols-5">
        <DefaultBar />
      </div>
      {moreOpen && <MoreSheet />}
    </nav>
  );
};

export default MobileBottomBar;
